import React, { useState, useEffect } from 'react';
import SideMenu from "../../component/SideMenu";
import Topbar from "../../component/Topbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import { baseurl } from '../../baseurl';
import { Edit3, Box, Zap, Clock, Map } from 'lucide-react';
import { Link } from 'react-router-dom';

const AllReports = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
 
  const adminToken = localStorage.getItem('adminToken');

  useEffect(() => {
    const fetchPackages = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${baseurl}getAllServicePackages`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        if (response.data.success) {
          setPackages(response.data.data);
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch service packages' });
      } finally {
        setTimeout(() => setLoading(false), 600);
      }
    };
    fetchPackages();
  }, [adminToken]);

  // --- Logic: Sorting ---
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sortedData = [...packages].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // --- Logic: Filtering ---
  const filteredData = sortedData.filter(p => 
    `${p.name} ${p.description}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // --- Logic: Pagination ---
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const SkeletonRow = () => (
    <tr className="skeleton-row">
      <td colSpan="7"><div className="skeleton-line"></div></td>
    </tr>
  );

  return (
    <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', display: 'flex' }}>
        {/* <SideMenu /> */}
        <div className="profile-content-container p-4" style={{ flex: 1, overflow: 'auto' }}>
          
          <div className="d-flex justify-content-between align-items-end mb-4">
            <div>
              <h2 className="fw-bold mb-1" style={{ color: '#1B2559' }}>All Reports & Features</h2>
              <p className="text-muted m-0">Manage report templates, credit rates, and processing speeds.</p>
            </div>
            <Link to="/create-report" className="btn btn-primary rounded-pill px-4 shadow-sm">
              + Create New Template
            </Link>
          </div>

          {/* CONTROL BOX */}
          <div className="card border-0 shadow-sm mb-4 p-3" style={{ borderRadius: '15px' }}>
            <div className="row g-3 align-items-center">
              <div className="col-md-4">
                <div className="input-group border rounded-3 bg-light m-0">
                  <span className="input-group-text bg-transparent border-0"><i className="fa-solid fa-magnifying-glass text-muted"></i></span>
                  <input 
                    type="text" 
                    className="form-control bg-transparent border-0 shadow-none" 
                    placeholder="Search package name..." 
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <select className="form-select border-0 bg-light rounded-3" onChange={(e) => handleSort(e.target.value)}>
                  <option value="created_at">Sort by: Newest</option>
                  <option value="name">Sort by: Name</option>
                  <option value="credit_rate">Sort by: Credit Rate</option>
                </select>
              </div>
            </div>
          </div>

          {/* TABLE */}
          <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-4 border-0 text-muted small fw-bold">ID</th>
                    <th className="border-0 text-muted small fw-bold">TEMPLATE INFO</th>
                    <th className="border-0 text-muted small fw-bold text-center">CREDIT RATE</th>
                    <th className="border-0 text-muted small fw-bold text-center">UNIT (ACRE)</th>
                    <th className="border-0 text-muted small fw-bold text-center">EST. SPEED</th>
                    <th className="border-0 text-muted small fw-bold">CREATED</th>
                    <th className="border-0 text-muted small fw-bold text-center">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [1,2,3].map(i => <SkeletonRow key={i} />)
                  ) : currentRows.length > 0 ? (
                    currentRows.map((pkg) => (
                      <tr key={pkg.id}>
                        <td className="ps-4 text-muted">#{pkg.id}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="avatar-icon me-3"><Box size={18} /></div>
                            <div>
                              <div className="fw-bold text-dark">{pkg.name}</div>
                              <div className="small text-muted text-truncate" style={{maxWidth: '200px'}}>{pkg.description}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-center">
                           <span className="badge rounded-pill bg-soft-primary text-primary px-3">
                             <Zap size={12} className="me-1" /> {pkg.credit_rate} Cr
                           </span>
                        </td>
                        <td className="text-center text-dark fw-bold">
                          <Map size={14} className="me-1 text-muted" /> {pkg.base_unit_acre}
                        </td>
                        <td className="text-center">
                          <div className="small fw-bold text-dark">
                             <Clock size={12} className="me-1" /> {pkg.est_time_value} {pkg.est_time_unit}
                          </div>
                        </td>
                        <td className="text-muted small">{formatDate(pkg.created_at)}</td>
                        <td className="text-center">
                          <Link to={`/create-report?id=${pkg.id}&action=edit`} className="btn btn-outline-primary btn-sm px-3 rounded-pill">
                            <Edit3 size={14} className="me-1" /> Edit
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="7" className="text-center py-5 text-muted">No templates found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        .avatar-icon {
          width: 38px;
          height: 38px;
          background: #F4F7FE;
          color: #4318FF;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
        }
        .bg-soft-primary { background: #E9EDF7; }
        .skeleton-row td { padding: 25px 0; }
        .skeleton-line {
          height: 20px;
          width: 90%;
          margin: 0 auto;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite;
          border-radius: 4px;
        }
        @keyframes skeleton-loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

export default AllReports;